import { useState } from "react";
import { Link } from "react-router";
import { Badge, Input, PageHeader, Select } from "@fellowflats/ui";
import { AdminListingsSchema, AdminListingStatusSchema, type AdminListingRow } from "@fellowflats/contracts/admin";
import { api } from "../lib/api";
import { useApi } from "../lib/use-api";
import { Loaded, day } from "../components/bits";

export const words = (v: string) => v.toLowerCase().replace(/_/g, " ");
export const dollars = (cents: number | null) =>
  cents == null ? "-" : new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(cents / 100);

export const statusTone: Record<AdminListingRow["status"], "success" | "warning" | "neutral" | "danger" | "info"> = {
  ACTIVE: "success",
  DRAFT: "neutral",
  PENDING_REVIEW: "info",
  PAUSED: "warning",
  ARCHIVED: "danger",
};

/** Every listing on the site, whoever owns it. Open one to change anything in it. */
export function Listings() {
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  const [status, setStatus] = useState("");
  const qs = new URLSearchParams({ ...(term ? { q: term } : {}), ...(status ? { status } : {}) }).toString();
  const state = useApi(() => api("GET", `/admin/listings${qs ? `?${qs}` : ""}`, AdminListingsSchema), qs);
  return (
    <div className="space-y-6">
      <PageHeader title="Listings" description="Every listing, any status, any owner. Recently changed first, 200 at most. Open one to edit it." />
      <div className="flex flex-wrap gap-2">
        <form
          className="min-w-64 flex-1"
          onSubmit={(e) => {
            e.preventDefault();
            setTerm(q.trim());
          }}
        >
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, city or owner, then Enter" aria-label="Search listings" />
        </form>
        <div className="w-48">
          <Select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
            <option value="">Any status</option>
            {AdminListingStatusSchema.options.map((s) => (
              <option key={s} value={s}>
                {words(s)}
              </option>
            ))}
          </Select>
        </div>
      </div>
      <Loaded state={state}>
        {(rows) =>
          rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No listings match.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-left text-sm">
                <thead className="bg-muted text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Listing</th>
                    <th className="px-3 py-2">Owner</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">From</th>
                    <th className="px-3 py-2">Beds free</th>
                    <th className="px-3 py-2">Changed</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((l) => (
                    <tr key={l.id} className="border-t border-border hover:bg-muted/50">
                      <td className="px-3 py-2">
                        <Link to={`/listings/${l.id}`} className="font-medium text-primary hover:underline">
                          {l.title}
                        </Link>
                        <p className="text-muted-foreground">
                          {l.city}, {l.province} · {l.listingType === "ENTIRE_UNIT" ? "whole unit" : "by the bed"} · {l.photos} photo(s)
                        </p>
                      </td>
                      <td className="px-3 py-2">
                        <p>{l.owner.fullName ?? "-"}</p>
                        <p className="text-muted-foreground">{l.owner.email}</p>
                      </td>
                      <td className="space-x-1 px-3 py-2">
                        <Badge tone={statusTone[l.status]}>{words(l.status)}</Badge>
                        {l.isFeatured && <Badge tone="primary">featured</Badge>}
                        {l.verificationStatus === "APPROVED" && <Badge tone="success">verified</Badge>}
                        {!l.owner.verifiedLandlord && <Badge tone="warning">owner unverified</Badge>}
                      </td>
                      <td className="px-3 py-2">{dollars(l.fromCents)}</td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {l.availableSlots} / {l.totalSlots}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{day(l.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </Loaded>
    </div>
  );
}
